/**
 * 🟦 V11.0 Phase 7 — Bridge Lyon ↔ UI Builder.
 *
 * Pequeño event bus + estado en window para que el panel flotante de Lyon
 * (montado en el Header, fuera del árbol del Builder) pueda leer el nodo
 * actualmente seleccionado en el UI Builder y opcionalmente adjuntarlo
 * (con o sin screenshot) al chat con la IA.
 *
 * Diseño:
 *   - El UI Builder llama `publishBuilderSelection(selected)` cada vez que
 *     cambia `selected`. Esto dispara un CustomEvent y escribe
 *     `window.__damyxiaBuilderSelection` para que Lyon pueda leerlo de
 *     forma síncrona al abrir el panel (sin esperar a un nuevo evento).
 *   - LyonHeaderButton llama `subscribeBuilderSelection(cb)` en mount y
 *     se desuscribe en unmount.
 *   - `getBuilderIframe()` busca el iframe del Builder por data-testid.
 *
 * NO contiene estado React; es un singleton liviano basado en `window`.
 */

const EVENT_NAME = 'damyxia:builder-selection-change';
const WINDOW_KEY = '__damyxiaBuilderSelection';

/**
 * Convierte el objeto `selected` interno del Builder en un payload
 * compacto y serializable para Lyon (recorta arrays/objetos grandes).
 * Devuelve null si no hay selección.
 */
function summarizeSelection(selected) {
  if (!selected || !selected.uib_id) return null;
  // Limitar tamaños para no inflar el contexto del LLM.
  const className = (selected.className || '').toString().slice(0, 600);
  const text = (selected.text || '').toString().slice(0, 600);
  const stylesEntries = Object.entries(selected.styles || {}).slice(0, 40);
  const attrsEntries = Object.entries(selected.attrs || {})
    .filter(([k]) => k !== 'data-uib-id' && k !== 'data-uib-selected')
    .slice(0, 20);
  const responsive = selected.responsive || { lg: {}, md: {}, sm: {} };
  return {
    uib_id: selected.uib_id,
    tag: (selected.tag || '').toString().toLowerCase(),
    text,
    className,
    styles: Object.fromEntries(stylesEntries),
    attrs: Object.fromEntries(attrsEntries),
    responsive: {
      lg: { ...(responsive.lg || {}) },
      md: { ...(responsive.md || {}) },
      sm: { ...(responsive.sm || {}) },
    },
  };
}

/**
 * Publica el nodo seleccionado actual del Builder. Llamar con `null`
 * cuando se deselecciona o el Builder se desmonta.
 */
export function publishBuilderSelection(selected) {
  if (typeof window === 'undefined') return;
  const summary = summarizeSelection(selected);
  window[WINDOW_KEY] = summary;
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: summary }));
  } catch {
    /* ignore — entornos no-DOM */
  }
}

/**
 * Suscribe un callback a cambios de selección. Devuelve función de unsubscribe.
 * El callback recibe `summary | null`.
 */
export function subscribeBuilderSelection(callback) {
  if (typeof window === 'undefined' || typeof callback !== 'function') {
    return () => {};
  }
  const handler = (e) => {
    try {
      callback(e?.detail ?? null);
    } catch {
      /* swallow handler errors */
    }
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

/** Lee la selección actual sin suscribirse (síncrono). */
export function getCurrentBuilderSelection() {
  if (typeof window === 'undefined') return null;
  return window[WINDOW_KEY] || null;
}

/** Devuelve el iframe del Builder si está montado, si no null. */
export function getBuilderIframe() {
  if (typeof document === 'undefined') return null;
  return document.querySelector('[data-testid="uib-iframe"]');
}

// 🟦 V11.0 Phase 7+ — "Aplicar como patch" desde Lyon.
// Lyon pide aplicar una sugerencia como JSON Patch al nodo actualmente
// seleccionado. UiBuilderPage subscribe y dispara handleAIEditNode con la
// instrucción. La respuesta vuelve como otro evento para que Lyon pueda
// mostrar feedback success/error sin acoplar React.

const APPLY_REQUEST_EVENT = 'damyxia:builder-apply-instruction-request';
const APPLY_RESULT_EVENT = 'damyxia:builder-apply-instruction-result';

/**
 * Lyon pide aplicar una instrucción como patch. Devuelve un Promise que
 * resuelve cuando UiBuilderPage responde con el resultado (success/error).
 * `requestId` se inyecta automáticamente para correlacionar request/result.
 *
 * 🟦 V11.1 — `opts.nodeContext` permite forzar que la edición se aplique
 * sobre un nodo recordado (memoria multi-paso de Lyon) en vez del nodo
 * actualmente seleccionado en el inspector. Si no se pasa, UiBuilderPage
 * usa su estado `selected` (comportamiento original).
 */
export function requestApplyInstruction(instruction, opts = {}) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('no_window'));
  }
  const requestId = `lyonapply-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const timeoutMs = Number.isFinite(opts.timeoutMs) ? opts.timeoutMs : 30000;
  const nodeContext = opts.nodeContext || null;
  return new Promise((resolve) => {
    let done = false;
    const onResult = (e) => {
      const d = e?.detail || {};
      if (d.requestId !== requestId) return;
      if (done) return;
      done = true;
      window.removeEventListener(APPLY_RESULT_EVENT, onResult);
      clearTimeout(timer);
      resolve(d);
    };
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      window.removeEventListener(APPLY_RESULT_EVENT, onResult);
      resolve({ requestId, ok: false, error: 'timeout' });
    }, timeoutMs);
    window.addEventListener(APPLY_RESULT_EVENT, onResult);
    try {
      window.dispatchEvent(new CustomEvent(APPLY_REQUEST_EVENT, {
        detail: {
          requestId,
          instruction: String(instruction || '').slice(0, 4000),
          nodeContext,
        },
      }));
    } catch {
      done = true;
      clearTimeout(timer);
      window.removeEventListener(APPLY_RESULT_EVENT, onResult);
      resolve({ requestId, ok: false, error: 'dispatch_failed' });
    }
  });
}

/**
 * UiBuilderPage subscribe para procesar las solicitudes de aplicación.
 * `callback({requestId, instruction})` debe llamar `publishApplyResult` cuando
 * termine (con ok=true/false y opcionalmente detalles).
 */
export function subscribeApplyRequest(callback) {
  if (typeof window === 'undefined' || typeof callback !== 'function') {
    return () => {};
  }
  const handler = (e) => {
    try {
      callback(e?.detail || {});
    } catch {
      /* swallow */
    }
  };
  window.addEventListener(APPLY_REQUEST_EVENT, handler);
  return () => window.removeEventListener(APPLY_REQUEST_EVENT, handler);
}

/** Publica el resultado de una solicitud (lo consume el Promise de Lyon).
 *  🟦 V11.1 — `updatedNodeContext` permite a Lyon refrescar su memoria
 *  con el estado post-patch del nodo (para chains multi-paso coherentes).
 *  🟦 V11.3 — `patch` propaga el JSON Patch RFC 6902 efectivo para que
 *  recipesStore pueda re-aplicarlo a otros nodos. */
export function publishApplyResult({ requestId, ok, error = null, summary = null, updatedNodeContext = null, patch = null }) {
  if (typeof window === 'undefined' || !requestId) return;
  try {
    window.dispatchEvent(new CustomEvent(APPLY_RESULT_EVENT, {
      detail: { requestId, ok: !!ok, error, summary, updatedNodeContext, patch },
    }));
  } catch {
    /* ignore */
  }
}
